'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { X, Loader2, CheckCircle2 } from 'lucide-react'

const ADDONS = [
  { package_key: 'homeschool_single', name: 'Standard',       desc: 'All 84 standard subjects + Faith stream',      price: 17.5 },
  { package_key: 'edu_elite',         name: 'Elite Academy',  desc: 'All 30 classical mentorship courses + Faith', price: 22.5 },
  { package_key: 'edu_bundle',        name: 'Complete Bundle', desc: 'Everything: 84 + 30 + Faith stream',          price: 29.5 },
]

// Same token set as the progress page / quiz table
const C = {
  card: '#1A2336', tile: '#212D49', line: 'rgba(255,255,255,0.09)',
  text: '#F2F5FA', muted: '#A6AEC0',
  gold: '#C9A85C', goldSoft: '#D4B36A', goldTint: 'rgba(201,168,92,0.13)',
  goldLine: 'rgba(201,168,92,0.55)', rose: '#EE9EB0', ink: '#141B2E',
}
const goldBtn = `linear-gradient(135deg, ${C.goldSoft}, ${C.gold})`

export default function AddSelfLearnModal({ isOpen, onClose, currentPlan }: {
  isOpen: boolean; onClose: () => void; currentPlan?: string
}) {
  const navigate = useNavigate()
  const { refreshUserProfile } = useAuth()
  const [selected, setSelected] = useState<string>(
    currentPlan && ADDONS.some(a => a.package_key === currentPlan) ? currentPlan : 'edu_elite'
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => { if (isOpen) { setDone(false); setError('') } }, [isOpen])

  async function handleEnroll() {
    setLoading(true); setError('')
    try {
      const { error: fnErr, data } = await supabase.functions.invoke('add-family-learner', {
        body: { is_self: true, package_key: selected },
      })
      if (fnErr) throw new Error(fnErr.message)
      if (data?.error) throw new Error(data.error)
      await refreshUserProfile()          // flips preferences.self_learn_active → smart links appear
      setDone(true)
    } catch (e: any) {
      setError(e.message || 'Enrollment failed')
    } finally { setLoading(false) }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(9,12,22,0.62)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
          onClick={() => !loading && onClose()}>
          <motion.div initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
            onClick={e => e.stopPropagation()}
            style={{
              background: C.card, border: `1px solid ${C.line}`, borderRadius: 20,
              maxWidth: 448, width: '100%', padding: 24,
              boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
              fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
              display: 'flex', flexDirection: 'column', gap: 16,
            }}>
            {!done ? (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: 0 }}>Study Yourself — 50% Off</h3>
                    <p style={{ fontSize: 13.5, color: C.muted, marginTop: 6, lineHeight: 1.5, marginBottom: 0 }}>
                      With any active child subscription, add your own studies at half price. Access starts today; billing begins at your next renewal.
                    </p>
                  </div>
                  <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 4 }}>
                    <X size={18} />
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ADDONS.map(a => {
                    const isSel = selected === a.package_key
                    return (
                      <button key={a.package_key} onClick={() => setSelected(a.package_key)}
                        style={{
                          width: '100%', textAlign: 'left', padding: 14, borderRadius: 14, cursor: 'pointer',
                          border: `1.5px solid ${isSel ? C.goldLine : C.line}`,
                          background: isSel ? 'rgba(201,168,92,0.10)' : C.tile,
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                        onMouseEnter={e => { if (!isSel) e.currentTarget.style.borderColor = 'rgba(201,168,92,0.35)' }}
                        onMouseLeave={e => { if (!isSel) e.currentTarget.style.borderColor = C.line }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{a.name}</div>
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{a.desc}</div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: C.goldSoft }}>${a.price.toFixed(2)}</div>
                            <div style={{ fontSize: 11, color: C.muted, fontWeight: 800, letterSpacing: '0.06em' }}>/MONTH · 50% OFF</div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>

                {error && <p style={{ fontSize: 12, color: C.rose, margin: 0 }}>{error}</p>}

                <button onClick={handleEnroll} disabled={loading}
                  style={{
                    width: '100%', padding: '12px 0', borderRadius: 13, border: 'none',
                    background: goldBtn, color: C.ink, fontSize: 14, fontWeight: 800,
                    cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                  {loading
                    ? <><Loader2 size={15} className="animate-spin" /> Adding your studies…</>
                    : <>Add My Studies — ${ADDONS.find(a => a.package_key === selected)?.price.toFixed(2)}/mo</>}
                </button>
              </>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <CheckCircle2 size={40} color={C.gold} style={{ margin: '0 auto 12px', display: 'block' }} />
                <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: '0 0 6px' }}>You're enrolled!</h3>
                <p style={{ fontSize: 14, color: C.muted, margin: '0 0 18px' }}>Your courses are on your learning dashboard, ready to start.</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={onClose}
                    style={{
                      flex: 1, padding: '11px 0', borderRadius: 12,
                      border: `1px solid ${C.line}`, background: 'transparent',
                      color: C.muted, fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                    }}>Later</button>
                  <button onClick={() => { onClose(); navigate('/learn') }}
                    style={{
                      flex: 1, padding: '11px 0', borderRadius: 12, border: 'none',
                      background: goldBtn, color: C.ink, fontSize: 13.5, fontWeight: 800, cursor: 'pointer',
                    }}>Go to My Learning →</button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}